/**
 * KAN-862: вкладки колонок мобильной доски: активная колонка по прокрутке
 * (IntersectionObserver внутри горизонтального scroll-snap) и прокрутка к
 * колонке по нажатию на вкладку.
 * По тапу прокручивается сам скроллер (scrollTo по offsetLeft колонки), а не
 * scrollIntoView: тот может сдвинуть и страницу по вертикали. Вкладка
 * становится активной сразу, и пока идёт программная прокрутка, наблюдатель
 * её не переключает (иначе вкладки мигают по промежуточным колонкам).
 * В окружении без IntersectionObserver или scrollTo (jsdom) вкладки просто
 * переключают активную колонку.
 * KAN-882: колонка по умолчанию (defaultKey) вместо первой; пока пользователь
 * ничего не выбрал, скроллер встаёт на неё сам. Выбор переживает обновление
 * данных и сбрасывается при смене resetKey (вид доски).
 * KAN-883: выбор можно держать снаружи (choice + onChoiceChange): тогда он
 * переживает перемонтирование доски (фильтр "Требуют внимания"); сброс - у
 * владельца состояния. После перезапроса (фильтры, период, менеджер) скроллер
 * возвращается к активной колонке, если она есть в новом наборе: прокрутка заново
 * смонтированного скроллера с нуля не перетирает выбор.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const COLUMN_SELECTOR = '[data-column-key]';

/** Сколько игнорировать наблюдатель после тапа по вкладке (плавная прокрутка). */
export const PROGRAMMATIC_SCROLL_MS = 500;

export interface SnapColumnTabsOptions {
  /** Колонка, активная, пока пользователь не выбрал другую; нет: первая. */
  defaultKey?: string | null;
  /** Смена значения сбрасывает выбор пользователя (только при выборе внутри хука). */
  resetKey?: string;
  /** KAN-883: выбор снаружи (вместе с onChoiceChange). */
  choice?: string | null;
  onChoiceChange?: (key: string) => void;
}

export function useSnapColumnTabs(keys: readonly string[], enabled: boolean, options: SnapColumnTabsOptions = {}) {
  const { defaultKey = null, resetKey = '', onChoiceChange } = options;
  const controlled = onChoiceChange !== undefined;
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const ignoreObserverUntil = useRef(0);
  const [ownChoice, setOwnChoice] = useState<string | null>(null);
  const [seenReset, setSeenReset] = useState(resetKey);
  if (!controlled && seenReset !== resetKey) {
    // Смена вида: выбор пользователя сбрасывается (React перерисует сразу, до отрисовки).
    setSeenReset(resetKey);
    setOwnChoice(null);
  }
  const choice = controlled ? (options.choice ?? null) : seenReset === resetKey ? ownChoice : null;
  const onChoiceRef = useRef(onChoiceChange);
  onChoiceRef.current = onChoiceChange;
  const setChoice = useCallback(
    (key: string) => (onChoiceRef.current ? onChoiceRef.current(key) : setOwnChoice(key)),
    []
  );
  const selected = choice !== null && keys.includes(choice) ? choice : null;
  const fallback = defaultKey && keys.includes(defaultKey) ? defaultKey : (keys[0] ?? null);
  const active = selected ?? fallback;
  const keysSignature = keys.join('|');
  // Для наблюдателя: колонка по умолчанию, пока пользователь ничего не выбрал, выбором не считается.
  const selectedRef = useRef<string | null>(selected);
  const fallbackRef = useRef<string | null>(fallback);
  const activeRef = useRef<string | null>(active);
  selectedRef.current = selected;
  fallbackRef.current = fallback;
  activeRef.current = active;

  const scrollToKey = useCallback((key: string, behavior: ScrollBehavior) => {
    const scroller = scrollerRef.current;
    const target = Array.from(scroller?.querySelectorAll<HTMLElement>(COLUMN_SELECTOR) ?? []).find(
      (el) => el.getAttribute('data-column-key') === key
    );
    if (!scroller || !target || typeof scroller.scrollTo !== 'function') return;
    // Плавная прокрутка идёт через промежуточные колонки: наблюдатель на это время молчит.
    // Мгновенная ('instant' сильнее CSS scroll-smooth карусели) промежуточных колонок не даёт.
    if (behavior === 'smooth') ignoreObserverUntil.current = Date.now() + PROGRAMMATIC_SCROLL_MS;
    // Скроллер position: relative, поэтому offsetLeft колонки отсчитан от него
    // в координатах прокрутки. Минус левый отступ скроллера: колонка встаёт
    // туда же, куда её ставит snap при свайпе.
    const padding = Number.parseFloat(getComputedStyle(scroller).paddingLeft) || 0;
    scroller.scrollTo({ left: Math.max(0, target.offsetLeft - padding), behavior });
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!enabled || !scroller || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < ignoreObserverUntil.current) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const key = visible?.target.getAttribute('data-column-key');
        if (!key || (selectedRef.current === null && key === fallbackRef.current)) return;
        setChoice(key);
      },
      { root: scroller, threshold: [0.6] }
    );
    scroller.querySelectorAll(COLUMN_SELECTOR).forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [enabled, keysSignature, setChoice]);

  // Пока выбора нет, скроллер стоит на колонке по умолчанию; после нового набора колонок
  // (перезапрос, скроллер смонтирован заново) - на активной колонке. Мгновенно, без анимации:
  // промежуточных колонок нет, первый отчёт наблюдателя приходит уже по целевой колонке.
  // Свайп пользователя меняет active, но сюда не ведёт: прокрутка не дёргается обратно.
  const defaultSync = selected === null ? active : null;
  useEffect(() => {
    const target = activeRef.current;
    if (!enabled || target === null) return;
    scrollToKey(target, 'instant');
  }, [enabled, defaultSync, keysSignature, scrollToKey]);

  const selectTab = useCallback(
    (key: string) => {
      setChoice(key);
      scrollToKey(key, 'smooth');
    },
    [scrollToKey, setChoice]
  );

  return { scrollerRef, active, selectTab };
}
